'use client';

import {
  createContext,
  useContext,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { usePathname } from '@wilkins-software/redwood-utils';

interface NavigationContextType {
  isMenuOpen: boolean;
  setIsMenuOpen: (open: boolean) => void;
  pathname: string;
  isLoaded: boolean;
  currentUrl: string;
}

const NavigationContext = createContext<NavigationContextType | undefined>(
  undefined,
);

function subscribeToNoop() {
  return () => {
    // no-op: client mount snapshot is static after hydration
  };
}

function getClientIsLoadedSnapshot() {
  return true;
}

function getServerIsLoadedSnapshot() {
  return false;
}

function getClientCurrentUrlSnapshot() {
  return window.location.href;
}

function getServerCurrentUrlSnapshot() {
  return '';
}

export function NavigationProvider({ children }: { children: ReactNode }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const pathname = usePathname();
  const isLoaded = useSyncExternalStore(
    subscribeToNoop,
    getClientIsLoadedSnapshot,
    getServerIsLoadedSnapshot,
  );
  const currentUrl = useSyncExternalStore(
    subscribeToNoop,
    getClientCurrentUrlSnapshot,
    getServerCurrentUrlSnapshot,
  );

  return (
    <NavigationContext.Provider
      value={{
        isMenuOpen,
        setIsMenuOpen,
        pathname,
        isLoaded,
        currentUrl,
      }}
    >
      {children}
    </NavigationContext.Provider>
  );
}

export function useNavigation() {
  const context = useContext(NavigationContext);
  if (context === undefined) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
}
